type MaybeMerchantUser = {
  getMerchantQuery?: Record<string, any>;
  getUserQuery?: Record<string, any>;
  [key: string]: any;
};

const truthyValues = new Set(["1", "true", "yes", "enabled", "active"]);

const parseList = (value?: string) =>
  value
    ?.split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean) ?? [];

const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? "";

const getMerchant = (user?: MaybeMerchantUser | null) =>
  user?.getMerchantQuery ?? user?.getUserQuery ?? user ?? {};

const hasTruthyFlag = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return truthyValues.has(value.toLowerCase());
  return false;
};

const collectIdentifiers = (value: unknown): string[] => {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectIdentifiers(item));
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return [
      ...collectIdentifiers(record._id),
      ...collectIdentifiers(record.id),
      ...collectIdentifiers(record.school_id),
      ...collectIdentifiers(record.schoolId),
      ...collectIdentifiers(record.email_id),
    ];
  }
  const normalized = normalize(String(value));
  return normalized ? [normalized] : [];
};

export const getConfiguredAllowedSchoolIds = (): string[] => {
  const envValue =
    process.env.REACT_APP_PAYOUT_ALLOWED_SCHOOL_IDS ||
    process.env.REACT_APP_PAYOUT_ALLOWED_SCHOOL_ID ||
    process.env.REACT_APP_PAYOUT_SCHOOL_ALLOWLIST ||
    process.env.REACT_APP_PAYOUT_MERCHANT_ALLOWLIST ||
    "";
  return parseList(envValue);
};

export const isUserInAllowedSchool = (user?: MaybeMerchantUser | null): boolean => {
  const allowed = getConfiguredAllowedSchoolIds();
  if (allowed.length === 0) return false;

  const merchant = getMerchant(user);
  const userIdentifiers = [
    ...collectIdentifiers(merchant?.school_id),
    ...collectIdentifiers(merchant?.school),
    ...collectIdentifiers(merchant?.getSchoolQuery),
    ...collectIdentifiers(merchant?.trustee_id),
    ...collectIdentifiers(merchant?._id),
    ...collectIdentifiers(merchant?.email_id),
  ];

  return allowed.some((id) => userIdentifiers.includes(id));
};

/** School-level flag from trustee getMerchantQuery. */
export const isSchoolPayoutsEnabled = (
  user?: MaybeMerchantUser | null,
): boolean => {
  const merchant = getMerchant(user);
  return (
    hasTruthyFlag(merchant?.payouts_enabled) ||
    hasTruthyFlag(merchant?.payout_feature_enabled) ||
    hasTruthyFlag(merchant?.featureFlags?.payouts) ||
    hasTruthyFlag(merchant?.features?.payouts) ||
    hasTruthyFlag(merchant?.features?.payouts_enabled) ||
    hasTruthyFlag(merchant?.school?.payouts_enabled) ||
    hasTruthyFlag(merchant?.getSchoolQuery?.payouts_enabled)
  );
};

export const getMerchantSchoolId = (
  user?: MaybeMerchantUser | null,
): string => {
  const merchant = getMerchant(user);
  const raw = merchant?.school_id ?? merchant?.schoolId;
  if (Array.isArray(raw)) return String(raw[0] || "").trim();
  return String(raw || "").trim();
};

export const getMerchantEmail = (user?: MaybeMerchantUser | null): string => {
  const merchant = getMerchant(user);
  return String(merchant?.email_id || merchant?.email || "")
    .trim()
    .toLowerCase();
};

const shouldBypassPayoutAccessGate = () =>
  process.env.REACT_APP_PAYOUTS_ACCESS_BYPASS === "true" &&
  process.env.NODE_ENV !== "production";

const isNonProdEnvAllowlistActive = () =>
  process.env.NODE_ENV !== "production" &&
  getConfiguredAllowedSchoolIds().length > 0;

/**
 * Sync school-level gate only (does not prove user enrollment).
 * Production nav must also require a successful merchant-exchange probe.
 */
export const canAccessPayoutsSchool = (
  user?: MaybeMerchantUser | null,
): boolean => {
  if (shouldBypassPayoutAccessGate()) return true;
  if (isNonProdEnvAllowlistActive()) return isUserInAllowedSchool(user);
  return isSchoolPayoutsEnabled(user);
};

/**
 * @deprecated Prefer canAccessPayoutsSchool + enrollment probe in Dashboard.
 * Kept for callers that only have sync context; treats school flag as gate.
 */
export const canAccessPayouts = (user?: MaybeMerchantUser | null): boolean =>
  canAccessPayoutsSchool(user);

export const isPayoutFeatureEnabled = isSchoolPayoutsEnabled;

export const hasPayoutUserAccess = (): boolean => false;

export type ApprovalGroupApiStage = "MAKER" | "CHECKER" | "APPROVER" | "AUDITOR";

const payoutAccessRank: Record<string, number> = {
  observer: 1,
  "finance executive": 2,
  "finance manager": 3,
  administrator: 4,
};

const normalizeAccessLevel = (value?: string | null) =>
  value?.trim().toLowerCase() ?? "";

export const canUserJoinApprovalGroup = (accessLevel: string | undefined): boolean =>
  (payoutAccessRank[normalizeAccessLevel(accessLevel)] ?? 0) >= 2;

export const approvalStageFromLevelCode = (
  stage: "M" | "C" | "A" | "U",
): ApprovalGroupApiStage => {
  if (stage === "M") return "MAKER";
  if (stage === "A") return "APPROVER";
  if (stage === "U") return "AUDITOR";
  return "CHECKER";
};

export const approvalStageFromTitle = (title: string): ApprovalGroupApiStage => {
  if (title === "Maker") return "MAKER";
  if (title === "Approver") return "APPROVER";
  if (title === "Auditor") return "AUDITOR";
  return "CHECKER";
};

export const approvalStageTitleFromApi = (stage: ApprovalGroupApiStage): string => {
  if (stage === "MAKER") return "Maker";
  if (stage === "APPROVER") return "Approver";
  if (stage === "AUDITOR") return "Auditor";
  return "Checker";
};

export const APPROVAL_GROUP_STAGE_LABELS = [
  "Maker",
  "Checker",
  "Approver",
  "Auditor",
] as const;

export const filterMemberOptionsForApprovalGroup = <
  T extends { accessLevel?: string },
>(
  options: T[],
): T[] => options.filter((option) => canUserJoinApprovalGroup(option.accessLevel));
